<template>
  <v-dialog
      ref="docViewerDialog"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="showDialog"
      max-width="100%"
      content-class="document-viewer"
      :hide-overlay="hideOverlay"
  >
    <v-card>
      <v-toolbar fixed flat>
        <v-row align="center">
          <v-col class="d-none d-sm-block">
            <v-toolbar-title class="text-caption" v-if="currentDoc">{{ title }}</v-toolbar-title>
          </v-col>
          <v-col class="d-xs-block d-sm-none"/> <!-- dummy column for xs-size (<600px width) to center rotation buttons-->
          <!-- Rotate Buttons -->
          <v-col v-if="currentDoc && currentDoc.fileType !== 'application/pdf'" class="d-sm-block text-center" align-self="center">
            <v-tooltip bottom>
              <template v-slot:activator="{on, attr}">
                <v-icon
                    @click="rotateClockwise"
                    class="mr-3 greyBold--text"
                    v-bind="attr"
                    v-on="on"
                >
                  rotate_right
                </v-icon>
              </template>
              <span>{{ $t('components:document-viewer.rotate-right') }}</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{on, attr}">
                <v-icon
                    @click="rotateAntiClockwise"
                    class="greyBold--text"
                    v-bind="attr"
                    v-on="on"
                >
                  rotate_left
                </v-icon>
              </template>
              <span>{{ $t('components:document-viewer.rotate-left') }}</span>
            </v-tooltip>
          </v-col>
          <v-col v-else></v-col>

          <!-- Close Button -->
          <v-col class="text-end">
            <v-icon
                @click="closeDialog()"
                class="greyBold--text"
            >
              close
            </v-icon>
          </v-col>
        </v-row>
      </v-toolbar>
      <v-card-text ref="vCardText" class="pa-0">
        <!-- Document switcher -->
        <div v-if="src.length > 1" class="doc-switcher">
          <v-tooltip bottom>
            <template v-slot:activator="{on, attr}">
              <v-icon
                  x-large
                  color="primary"
                  @click="prevDoc"
                  class="prev-doc"
                  v-bind="attr"
                  v-on="on"
              >
                navigate_before
              </v-icon>
            </template>
            <span>{{ $t('components:document-viewer.prev-doc') }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template v-slot:activator="{on, attr}">
              <v-icon
                  x-large
                  color="primary"
                  @click="nextDoc"
                  class="next-doc"
                  v-bind="attr"
                  v-on="on"
              >
                navigate_next
              </v-icon>
            </template>
            <span>{{ $t('components:document-viewer.next-doc') }}</span>
          </v-tooltip>
        </div>

        <div v-if="currentDoc && currentDoc.fileType === 'application/pdf'" class="container px-16 py-5">
          <p>{{ $t('components:document-viewer.pdf-other-tab') }}</p>
          <BaseButton
              :btn-text="$t('components:document-viewer.open-pdf')"
              @clickedButton="openPDFInNewTab"
              color="primary"
          />
        </div>

        <img
            v-else-if="currentDoc"
            id="image"
            :src="docSource"
            alt="Image"
            ref="image"
            @load="onImageLoad"
            @click="onImageClick"
            :style="'width: '+ imgWidth + 'px; height: ' + imgHeight +
              'px; top:'+ imgY + 'px; left: '+ imgX +'px; ' +
               'transform: rotate(' + rotate + 'deg) translate(' + imgTranslateString +
               ') scale(' + scalePercent + '%,' + scalePercent +'%);' "
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import DocumentationService from '@/modules/core/services/documentation-service';
import BaseButton from "@/modules/core/components/Base/BaseButton";

export default {
  name: 'DocumentViewer',
  components: {BaseButton},
  props: {
    qrCodeForPublic: String,
    offline: Boolean,
    public: Boolean,
    hideOverlay: Boolean
  },
  data() {
    return {
      showDialog: false,
      src: [], // all documents from table
      numPages: 0, // number of pages of document
      page: 1, // current page to display
      rotate: 0, // val for rotating document
      currentDoc: undefined, // document that is currently displayed
      docSource: '', //source of the current documentation
      relationId: null, // UserId or FacilityId
      documentType: '', // Current DocumentType(USER, STEP, FACILITY)

      imgWidth: 1,    // low dummy value
      imgHeight: 1,
      imgX: 0,
      imgY: 0,
      imgTranslateString: '0%,0%',
      scalePercent: 100,
    }
  },
  methods: {
    initViewer(docus, docToView, relationId) {
      this.src = docus;
      this.relationId = typeof relationId !== 'undefined' && relationId ? relationId : null;
      for (let i = 0; i < this.src.length; i++) {
        if (this.src[i].fileName === docToView) {
          this.currentDoc = this.src[i];
        }
      }
    },
    initViewerById(docus, docToView, relationId, documentType) {
      this.src = docus;
      this.documentType = documentType ? documentType : '';
      this.relationId = typeof relationId !== 'undefined' && relationId ? relationId : null;
      for (let i = 0; i < this.src.length; i++) {
        if (this.src[i].id === docToView) {
          this.currentDoc = this.src[i];
        }
      }
    },
    rotateClockwise() {
      this.rotate += 90;
      this.applyRotation();
    },
    rotateAntiClockwise() {
      this.rotate -= 90;
      this.applyRotation();
    },
    applyRotation() {
      if (this.currentDoc.fileType !== 'application/pdf') {
        this.rotate = (this.rotate + 360) % 360;
        this.recalculateImageSizeAndTransform();
      }
    },
    getArrayPosition() {
      for (let i = 0; i < this.src.length; i++) {
        if (this.src[i].id === this.currentDoc.id) {
          return i;
        }
      }
    },
    prevDoc() {
      this.rotate = 0;
      this.applyRotation();
      if (this.src[this.getArrayPosition() - 1]) {
        this.currentDoc = this.src[this.getArrayPosition() - 1];
      } else {
        this.currentDoc = this.src[this.src.length - 1];
      }
    },
    nextDoc() {
      this.rotate = 0;
      this.applyRotation();
      this.page = 1;
      if (this.src[this.getArrayPosition() + 1]) {
        this.currentDoc = this.src[this.getArrayPosition() + 1];
      } else {
        this.currentDoc = this.src[0];
      }
    },

    // converts a base64 string to a blob
    b64ToBlob (base64Data, contentType) {
      const byteCharacters = atob(base64Data);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);

        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      return new Blob(byteArrays, { type: contentType})
    },
    resetViewer() {
      this.src = [];
      this.currentDoc = null;
      this.docSource = null;
      this.rotate = 0;
    },
    closeDialog() {
      this.showDialog = false
      this.resetViewer();
    },
    openPDFInNewTab() {
      const base64Result = this.docSource.split('base64,')[1];

      // convert the base64 to a blob
      const blob = this.b64ToBlob(base64Result, this.currentDoc.fileType);
      // create url for the blob
      const blobUrl = URL.createObjectURL(blob);
      // open the blob in a new tab
      window.open(blobUrl, '_blank');
    },
    defaultLoader() {
      DocumentationService.getFileAsDataUrl(
          this.currentDoc.id,
          this.currentDoc.fileType,
          (result) => {
            this.docSource = result;

            // if the document is of type pdf
            // open the pdf in another tab, so he can view it
            if (this.currentDoc.fileType === 'application/pdf') {
              this.openPDFInNewTab();
            } else {
              this.showDialog = true;
            }
          }
      )
    },
    loadDocByTypeRelation() {
      DocumentationService.getDocByTypeAndId(
          this.currentDoc.id,
          this.currentDoc.fileType,
          this.documentType,
          this.relationId,
          (result) => {
            this.docSource = result;

            // if the document is of type pdf
            // open the pdf in another tab, so he can view it
            if (this.currentDoc.fileType === 'application/pdf') {
              this.openPDFInNewTab();
            } else {
              this.showDialog = true;
            }
          }
      )
    },
    loadDocByTypeRelationForPublic() {
      DocumentationService.getDocByTypeAndIdForPublic(
          this.currentDoc.id,
          this.currentDoc.fileType,
          this.documentType,
          this.relationId,
          this.$route.params.code,
          (result) => {
            this.docSource = result;

            // if the document is of type pdf
            // open the pdf in another tab, so he can view it
            if (this.currentDoc.fileType === 'application/pdf') {
              this.openPDFInNewTab();
            } else {
              this.showDialog = true;
            }
          }
      )
    },
    onImageLoad() {
      this.recalculateImageSizeAndTransform();
    },
    recalculateImageSizeAndTransform() {
      let img = this.$refs.image;
      let vCardText = this.$refs.vCardText;
      let deltaX = 50 + 2*10;
      // no reserved left/right space on small displays (<600pix width) or wen no next/prev doc is displayed
      if (this.$vuetify.breakpoint.xsOnly || this.src === 0 || this.src.length <= 1) {
        deltaX = 0;
      }
      let nw = img.naturalWidth;
      let nh = img.naturalHeight;
      let cw = vCardText.clientWidth - 2* deltaX; //2*50 pixel next/previous + 4*10px margin
      let ch = vCardText.clientHeight -1;
      let ww;
      let hh;
      let scaleFactor = 1.0;
      if (this.rotate % 180 === 0) {  // rotation 0° or 180°
        let fw = cw / nw;
        let fh = ch / nh;
        if (fw < fh) {  //  size limited by client-width
          ww = cw;
          hh = Math.floor(fw * nh) ;
          this.imgX = deltaX;
          this.imgY = (ch - hh)/2;
        }
        else {  // size limited by client-height
          ww = Math.floor(fh * nw);
          hh = ch;
          this.imgX = deltaX + (cw -Math.floor(fh * nw))/2 ;
          this.imgY = 0;
        }
      }
      else {  // rotation 90° or 270°
        let fw = cw / nh;
        let fh = ch / nw;
        if (fw < fh) { //size limited by client-width and rotated image-height
          hh = Math.floor(fw * nh);
          ww = Math.floor(fw * nw);
          this.imgX = deltaX + (cw - hh)/2;
          this.imgY = (ch-ww)/2;

        }
        else { //size limited by client-height and rotated image-width
          hh = Math.floor(fh * nh);
          ww = Math.floor(fh * nw);
          this.imgX = deltaX + (cw - hh)/2;
          this.imgY = (ch-ww)/2;

        }
        if (hh > ch) {
          scaleFactor = ch / hh;
        }
      }
      // rotation of the image may screw Vuetify-components generating holes,
      // since rotation and other transformations are not correctly adjusting visible space
      // resulting in scrollbars generating holes in dialogs, when scrolled
      // workaround: scale down  width/height and use an additional scale-up-transformation (incl. altering translate-percentages)
      //
      this.scalePercent = 100 * scaleFactor;  // used in html img style transform: scale(...) translate() via imgTransalteString
      this.imgHeight = hh / scaleFactor;
      this.imgWidth = ww / scaleFactor;
      if (this.rotate === 90) {
        this.imgTranslateString = '0%,-' + this.scalePercent + '%';
      }
      else if (this.rotate === 180) {
        this.imgTranslateString = '-'+ this.scalePercent +'%,-' + this.scalePercent + '%';
      }
      else if (this.rotate === 270) {
        this.imgTranslateString = '-' +  this.scalePercent + '%,0%';
      }
      else {
        this.imgTranslateString = '0%,0%';
      }
    },
    onImageClick() {
      //this.openPDFInNewTab();
      let image = new Image();
      image.src = this.docSource;

      let w = window.open("",'_blank');
      w.document.write(image.outerHTML);
      w.document.close();
    }
  },
  computed: {
    title() {if (!this.currentDoc) {
        return '';
      }
      let text =  this.currentDoc.name;
      if (this.src.length > 1) {
        text +=  ' [' + (this.getArrayPosition() + 1) + '/' + this.src.length + ']';
      }
      return text;
    }
  },
  watch: {
    currentDoc() {
      if (!this.currentDoc) {
        return;
      }

      if (!this.offline && !this.public) {
        if (this.documentType.length === 0 || !this.relationId) {
          this.defaultLoader();
        } else {
          this.loadDocByTypeRelation();
        }
      } else if (!this.offline && this.public) {
        this.loadDocByTypeRelationForPublic();
      } else {
        this.docSource = this.currentDoc.src;

        if (this.currentDoc.fileType === 'application/pdf') {
          this.openPDFInNewTab();
        } else {
          this.showDialog = true;
        }
      }
    }
  }
}
</script>
<style scoped src="@/modules/core/components/DocumentViewer/DocumentViewer.scss" lang="scss"/>

